/**
 * PWA Service
 * Handles service worker registration, install prompt, and app updates
 */

class PWAService {
  constructor() {
    this.deferredPrompt = null;
    this.swRegistration = null;
    this.isInstalled = false;
    this.isUpdateAvailable = false;
    this.listeners = {
      installable: [],
      installed: [],
      updateAvailable: [],
      offline: [],
      online: []
    };

    this._init();
  }

  _init() {
    // Check if already installed
    this.isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true;

    // Listen for install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this._emit('installable', true);
      console.log('[PWA] Install prompt available');
    });

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.deferredPrompt = null;
      this._emit('installed', true);
      console.log('[PWA] App installed successfully');
    });

    // Listen for online/offline
    window.addEventListener('online', () => {
      this._emit('online', true);
      console.log('[PWA] App is online');
    });

    window.addEventListener('offline', () => {
      this._emit('offline', true);
      console.log('[PWA] App is offline');
    });

    // Register service worker
    if ('serviceWorker' in navigator) {
      this._registerServiceWorker();
    }
  }

  async _registerServiceWorker() {
    try {
      this.swRegistration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });

      console.log('[PWA] Service worker registered:', this.swRegistration.scope);

      // Check for updates
      this.swRegistration.addEventListener('updatefound', () => {
        const newWorker = this.swRegistration.installing;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            this.isUpdateAvailable = true;
            this._emit('updateAvailable', true);
            console.log('[PWA] New version available');
          }
        });
      });

      // Handle controller change (update applied)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[PWA] Service worker updated, reloading...');
      });

      return this.swRegistration;
    } catch (error) {
      console.error('[PWA] Service worker registration failed:', error);
      throw error;
    }
  }

  /**
   * Show the install prompt
   * @returns {Promise<boolean>} Whether the user accepted the install
   */
  async promptInstall() {
    if (!this.deferredPrompt) {
      console.log('[PWA] No install prompt available');
      return false;
    }

    try {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;

      console.log('[PWA] Install prompt outcome:', outcome);

      if (outcome === 'accepted') {
        this.deferredPrompt = null;
        return true;
      }

      return false;
    } catch (error) {
      console.error('[PWA] Install prompt error:', error);
      return false;
    }
  }

  /**
   * Check if the app can be installed
   * @returns {boolean}
   */
  canInstall() {
    return this.deferredPrompt !== null && !this.isInstalled;
  }

  /**
   * Check if app is running as installed PWA
   * @returns {boolean}
   */
  isRunningAsPWA() {
    return this.isInstalled;
  }

  /**
   * Apply pending update (reload app)
   */
  applyUpdate() {
    if (this.swRegistration && this.swRegistration.waiting) {
      this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }

  /**
   * Check for service worker updates
   */
  async checkForUpdates() {
    if (this.swRegistration) {
      await this.swRegistration.update();
    }
  }

  /**
   * Get the service worker registration
   * @returns {ServiceWorkerRegistration|null}
   */
  getRegistration() {
    return this.swRegistration;
  }

  /**
   * Subscribe to PWA events
   * @param {string} event - Event name: 'installable', 'installed', 'updateAvailable', 'offline', 'online'
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }

  /**
   * Unsubscribe from PWA events
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  _emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  /**
   * Clear all caches
   * @returns {Promise<boolean>}
   */
  async clearCache() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('[PWA] Caches cleared');
      return true;
    }
    return false;
  }

  /**
   * Get online status
   * @returns {boolean}
   */
  isOnline() {
    return navigator.onLine;
  }
}

// Export singleton instance
export const pwaService = new PWAService();
export default pwaService;
